import * as React from "react"

import { cn } from "../../../lib/utils"
import { Icon } from "../icon/icon"
import type { IconName } from "../icon/icon-registry"

type EmptyStateVariant = "default" | "error"

const variantIcon: Record<EmptyStateVariant, IconName> = {
  default: "info",
  error: "alert-circle",
}

const variantIconBg: Record<EmptyStateVariant, string> = {
  default: "bg-border-subtle",
  error: "bg-destructive/10",
}

const variantIconColor: Record<EmptyStateVariant, string> = {
  default: "text-muted-foreground",
  error: "text-destructive",
}

export interface EmptyStateProps extends React.ComponentProps<"div"> {
  icon?: IconName
  variant?: EmptyStateVariant
  title: string
  description?: string
  action?: React.ReactNode
}

function EmptyState({
  className,
  icon,
  variant = "default",
  title,
  description,
  action,
  ...props
}: EmptyStateProps) {
  const resolvedIcon = icon ?? variantIcon[variant]

  return (
    <div
      data-slot="empty-state"
      className={cn(
        "flex flex-col items-center justify-center gap-4 py-12 text-center",
        className,
      )}
      {...props}
    >
      <div
        className={cn(
          "flex size-12 items-center justify-center rounded-full",
          variantIconBg[variant],
        )}
      >
        <Icon
          name={resolvedIcon}
          size="lg"
          className={variantIconColor[variant]}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <h3 className="text-lg font-semibold text-foreground">{title}</h3>
        {description ? (
          <p className="max-w-sm text-sm text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {action ? <div className="pt-2">{action}</div> : null}
    </div>
  )
}

export { EmptyState }
